import { ProgressFilesConfig } from "../agent-config-options";

/**
 * Default master switch for the progress-file convention. When no
 * config is supplied, the convention ships **enabled** so every phased
 * agent writes a progress file on claim and reads it on resume.
 *
 * @see ProgressFilesConfig
 */
export const DEFAULT_PROGRESS_FILES_ENABLED = true;

/**
 * Default on-disk root for progress files, relative to the repo root.
 * Every progress file resolves to
 * `<stateDir>/<filename>` where `<filename>` is produced from
 * `filenamePattern` at runtime.
 *
 * Lives at the top-level `.state/` directory so the path stays
 * harness-neutral — any agent runtime (Claude Code, Cursor, a
 * bespoke worker) can read and write the same progress files
 * without having to scope under a harness-specific tree.
 *
 * @see ProgressFilesConfig
 */
export const DEFAULT_PROGRESS_FILES_STATE_DIR = ".state";

/**
 * Default filename pattern for a progress file. The `<ISSUE_NUMBER>`
 * placeholder is substituted at runtime with the numeric id of the
 * issue the agent is working on (e.g. `479-progress.json`).
 *
 * The placeholder uses the angle-bracketed uppercase-snake form — not
 * `{{curly-brace}}` form — because `AgentConfig`'s template resolver
 * claims the curly-brace namespace at rule generation time.
 *
 * @see ProgressFilesConfig
 */
export const DEFAULT_PROGRESS_FILES_FILENAME_PATTERN =
  "<ISSUE_NUMBER>-progress.json";

/**
 * Default serialization format for a progress file body. JSON is the
 * default because it is trivially machine-parseable (e.g. for scripted
 * resume logic) while still remaining human-readable when opened.
 * Consumers that prefer the openhi-style markdown body can override.
 *
 * @see ProgressFilesConfig
 */
export const DEFAULT_PROGRESS_FILES_FORMAT: "json" | "markdown" = "json";

/**
 * Default stale-threshold (hours) for branches carrying a progress
 * file. When the orchestrator's stale-branch decision tree finds a
 * progress file older than this many hours **and** no matching open
 * PR, it treats the branch as abandoned and resets the issue to
 * `status:ready`. Mirrors the 72-hour in-progress threshold used by
 * the orchestrator bundle's triage walk.
 *
 * @see ProgressFilesConfig
 */
export const DEFAULT_PROGRESS_FILES_STALE_AFTER_HOURS = 72;

/**
 * Allowed values for `ProgressFilesConfig.format`. Exported so
 * consumers can reference the canonical set without hard-coding
 * literals.
 */
export const PROGRESS_FILES_FORMAT_VALUES = ["json", "markdown"] as const;

/**
 * Fully-resolved progress-file settings. Every field is defaulted so
 * downstream renderers can reason about a single canonical shape.
 */
export interface ResolvedProgressFiles {
  readonly enabled: boolean;
  readonly stateDir: string;
  readonly filenamePattern: string;
  readonly format: "json" | "markdown";
  readonly cleanupOnComplete: boolean;
  readonly staleAfterHours: number;
}

/**
 * Resolve a (possibly absent) `ProgressFilesConfig` into a canonical
 * `ResolvedProgressFiles` with every field filled in. Unset fields
 * cascade from their documented defaults.
 *
 * Malformed configs — empty / whitespace-only `stateDir`, absolute
 * `stateDir`, empty / whitespace-only `filenamePattern`, `filenamePattern`
 * missing the `<ISSUE_NUMBER>` placeholder, unknown `format` value,
 * non-positive `staleAfterHours` — throw a descriptive `Error`.
 */
export function resolveProgressFiles(
  config?: ProgressFilesConfig,
): ResolvedProgressFiles {
  const stateDir = config?.stateDir ?? DEFAULT_PROGRESS_FILES_STATE_DIR;
  assertValidStateDir(stateDir);

  const filenamePattern =
    config?.filenamePattern ?? DEFAULT_PROGRESS_FILES_FILENAME_PATTERN;
  assertValidFilenamePattern(filenamePattern);

  const format = config?.format ?? DEFAULT_PROGRESS_FILES_FORMAT;
  assertValidFormat(format);

  const staleAfterHours =
    config?.staleAfterHours ?? DEFAULT_PROGRESS_FILES_STALE_AFTER_HOURS;
  assertValidStaleAfterHours(staleAfterHours);

  return {
    enabled: config?.enabled ?? DEFAULT_PROGRESS_FILES_ENABLED,
    stateDir,
    filenamePattern,
    format,
    cleanupOnComplete: config?.cleanupOnComplete ?? true,
    staleAfterHours,
  };
}

/**
 * Synth-time validation hook. Throws a descriptive `Error` when the
 * supplied `ProgressFilesConfig` is malformed. Called by
 * `AgentConfig.preSynthesize` before any rendering so a misconfigured
 * convention fails the build instead of silently shipping broken
 * resume semantics. Returns the resolved config unchanged so callers
 * can write `const pf = validateProgressFilesConfig(config)` in
 * one line.
 *
 * Malformed cases rejected here:
 *
 * - `stateDir` empty, whitespace-only, or absolute.
 * - `filenamePattern` empty, whitespace-only, or missing the
 *   `<ISSUE_NUMBER>` placeholder.
 * - `format` not one of `"json"` / `"markdown"`.
 * - `staleAfterHours` non-integer, zero, or negative.
 */
export function validateProgressFilesConfig(
  config?: ProgressFilesConfig,
): ResolvedProgressFiles {
  return resolveProgressFiles(config);
}

/**
 * Resolve the runtime filename for a progress file given an issue
 * number and a resolved config. `<ISSUE_NUMBER>` placeholders in the
 * pattern are substituted; the returned value is **just** the filename
 * (no directory prefix).
 *
 * Exported so consumer-side scripts (or the `partial-resume-protocol`
 * rule renderer) can compute the on-disk path deterministically.
 */
export function renderProgressFileName(
  pf: ResolvedProgressFiles,
  issueNumber: number | string,
): string {
  return pf.filenamePattern.replace(/<ISSUE_NUMBER>/g, String(issueNumber));
}

/**
 * Resolve the runtime path (directory + filename) for a progress file
 * given an issue number and a resolved config.
 */
export function renderProgressFilePath(
  pf: ResolvedProgressFiles,
  issueNumber: number | string,
): string {
  return `${pf.stateDir}/${renderProgressFileName(pf, issueNumber)}`;
}

/**
 * Render the full body for the `progress-file-convention` rule shipped
 * by the `base` bundle. The rule documents:
 *
 * - The progress-file schema and on-disk path contract.
 * - The partial-resume protocol (read-before-write + acceptance
 *   criteria replay).
 * - The stale-branch decision tree (clone-level recovery) that every
 *   worker runs at session start.
 * - The `[BLOCKED]` structured comment format used when an agent
 *   cannot proceed.
 *
 * When the convention is disabled, the rule renders a short stub that
 * tells agents the project does not enforce progress files and they
 * must pick up work from scratch on every session.
 */
export function renderProgressFilesRuleContent(
  pf: ResolvedProgressFiles,
): string {
  if (!pf.enabled) {
    return [
      "# Progress File Convention",
      "",
      "**Progress files are disabled in this project.** Agents do not",
      "write a progress file when claiming an issue, and resumed",
      "sessions start from scratch rather than reading per-session",
      "state. The partial-resume protocol (below) still applies when an",
      "output file exists from a partial write — agents always check",
      "the Output Path before starting.",
      "",
      "Enable progress files via",
      "`AgentConfigOptions.progressFiles.enabled = true`.",
      "",
      ...partialResumeSection(),
      "",
      ...staleBranchSection(),
      "",
      ...blockedCommentSection(),
    ].join("\n");
  }

  const examplePath = renderProgressFilePath(pf, "<N>");
  const lines: Array<string> = [
    "# Progress File Convention",
    "",
    "Every phased agent session writes a small **progress file** to",
    "disk as its first action after claiming an issue, updates it after",
    "each non-trivial step, and deletes it in the final commit that",
    "closes the issue. If the session crashes, the next session reads",
    "the progress file, confirms on-disk state matches reality, and",
    "resumes from the next uncompleted step instead of starting over.",
    "",
    "Progress files are the **work-level** recovery mechanism. They",
    "complement the clone-level stale-branch recovery documented in",
    '"Stale Branch Recovery" below — that step restores the checkout',
    "to a clean main; this convention restores the *deliverable* to a",
    "partially-complete state that resumed work can build on.",
    "",
    "## On-disk Contract",
    "",
    `Progress files live at \`${examplePath}\`, where \`<N>\` is the`,
    "numeric issue id the agent is working on. The directory",
    `\`${pf.stateDir}\` is gitignored — progress files stay on the`,
    "working branch until the issue closes and are removed at that",
    "time; they must never land on the default branch.",
    "",
    `**Serialization format:** \`${pf.format}\`.`,
    "",
  ];

  if (pf.format === "json") {
    lines.push(
      "### Schema (JSON)",
      "",
      "```json",
      "{",
      '  "issue": <N>,',
      '  "agent": "<agent-name>",',
      '  "phase": "<phase-label>",',
      '  "started": "YYYY-MM-DDTHH:MM:SSZ",',
      '  "last_updated": "YYYY-MM-DDTHH:MM:SSZ",',
      '  "checkpoints": [',
      '    { "name": "read-inputs",       "status": "done",        "note": "N files" },',
      '    { "name": "draft-section-1",   "status": "done",        "note": "lines 1-45" },',
      '    { "name": "draft-section-2",   "status": "in-progress", "note": "60% done" },',
      '    { "name": "draft-section-3",   "status": "todo" }',
      "  ],",
      '  "decisions": [',
      '    "<key decision and rationale>"',
      "  ],",
      '  "next_action": "<what the next session should do first>"',
      "}",
      "```",
      "",
      "**Required fields:** `issue`, `agent`, `phase`, `started`,",
      "`last_updated`, `checkpoints`, `next_action`.",
      "",
      "**`checkpoints[].status`** is one of `todo`, `in-progress`,",
      "`done`. Always order entries to match execution order — the",
      "resumed agent walks the array left-to-right and picks up at the",
      "first non-`done` entry.",
      "",
      "**`decisions`** is optional and may be omitted when no key",
      "decisions have been made.",
      "",
    );
  } else {
    lines.push(
      "### Schema (Markdown)",
      "",
      "```markdown",
      "---",
      "issue: <N>",
      "agent: <agent-name>",
      "phase: <phase-label>",
      "started: YYYY-MM-DDTHH:MM:SSZ",
      "last_updated: YYYY-MM-DDTHH:MM:SSZ",
      "---",
      "",
      "## Progress",
      "- [x] Read inputs (N files)",
      "- [x] Section 1: <name> (written, lines 1-45)",
      "- [ ] Section 2: <name> (in progress, ~60% done)",
      "- [ ] Section 3: <name>",
      "",
      "## Decisions Made",
      "- <key decision and rationale>",
      "",
      "## Next Action",
      "<what the next session should do first>",
      "```",
      "",
      "Checkboxes map directly to checkpoint state: `[x]` is `done`,",
      "`[ ]` with inline progress text is `in-progress`, and a plain",
      "`[ ]` is `todo`.",
      "",
    );
  }

  lines.push(
    "## Write Rules",
    "",
    "1. **Write on claim.** Create the progress file as the first",
    "   action after claiming an issue — before reading inputs, before",
    "   starting any deliverable work. Commit the file in the same",
    "   commit that records the claim.",
    "2. **Update after each step.** After completing a section, reading",
    "   a key input, or making a significant decision, update the",
    "   progress file and include it in the next commit.",
    "3. **Commit progress alongside work.** Never commit deliverable",
    "   changes without also updating the progress file in the same",
    "   commit. Mixed commits keep the work-level recovery atomic.",
    "4. **Set `last_updated` on every write.** Use an ISO-8601",
    "   UTC timestamp (`YYYY-MM-DDTHH:MM:SSZ`). Stale `last_updated`",
    "   values feed the stale-branch decision tree below.",
  );

  if (pf.cleanupOnComplete) {
    lines.push(
      "5. **Delete on completion.** The final commit that closes the",
      "   issue must remove the progress file. Progress files are a",
      "   working-branch artefact; they do not land on the default",
      "   branch.",
    );
  } else {
    lines.push(
      "5. **Retention on completion.** This project retains progress",
      "   files on the default branch as an audit trail — the final",
      "   commit does **not** remove them. Downstream tooling that",
      "   scans `" + pf.stateDir + "` should skip completed issues by reading",
      "   the `phase` field.",
    );
  }

  lines.push(
    "",
    ...partialResumeSection(),
    "",
    ...staleBranchSection(pf.staleAfterHours),
    "",
    ...blockedCommentSection(),
  );

  return lines.join("\n");
}

/**
 * Render the short progress-file hook section injected into a
 * phased-agent bundle's workflow rule (bcm-writer, research-pipeline,
 * etc.). The section cites the full contract documented in the base
 * bundle's `progress-file-convention` rule so individual bundles stay
 * DRY.
 *
 * When the convention is disabled, the function returns an empty
 * string so callers can no-op their append path.
 */
export function renderProgressFilesBundleHook(
  pf: ResolvedProgressFiles,
  bundleLabel: string,
): string {
  if (!pf.enabled) {
    return "";
  }

  const examplePath = renderProgressFilePath(pf, "<N>");
  return [
    "## Progress File",
    "",
    `Every ${bundleLabel} session writes a progress file to`,
    `\`${examplePath}\` on claim, updates it after each phase step,`,
    "and deletes it in the final commit that closes the issue. See",
    "the `progress-file-convention` rule for the full schema, write",
    "rules, and partial-resume protocol.",
    "",
    "On startup, check for an existing progress file before doing any",
    "substantive work. If one exists, read it and continue from the",
    "`next_action` rather than starting the phase over.",
  ].join("\n");
}

/*******************************************************************************
 *
 * Internal helpers
 *
 ******************************************************************************/

function partialResumeSection(): Array<string> {
  return [
    "## Partial-Resume Protocol",
    "",
    "Before writing any Output Path file, always check whether the",
    "file already exists (from a previous crashed session or partial",
    "write). If it does:",
    "",
    "1. **Read the existing file** in full.",
    "2. **Compare it against the Acceptance Criteria** from the issue",
    "   body.",
    "3. **Identify which criteria are already met** by the existing",
    "   content.",
    "4. **Resume from the first unmet criterion** — do not rewrite",
    "   sections that already satisfy their criterion.",
    "5. **Comment on the issue:** `Resuming from partial deliverable",
    "   (N/M criteria already met).`",
    "",
    "### When the existing file is complete",
    "",
    "If the existing file satisfies **every** acceptance criterion:",
    "",
    "1. Verify cross-references are intact.",
    "2. Skip to the commit / PR step — the issue may already be done.",
    "3. Comment: `Output file already satisfies all acceptance",
    "   criteria. Proceeding to mark done.`",
    "",
    "This protocol applies to every agent and supersedes the generic",
    '"read it first" guidance. The progress file provides additional',
    "context about what was completed, but even without a progress",
    "file the partial-resume check ensures no work is lost.",
  ];
}

function staleBranchSection(staleAfterHours?: number): Array<string> {
  const hours = staleAfterHours ?? DEFAULT_PROGRESS_FILES_STALE_AFTER_HOURS;
  return [
    "## Stale Branch Recovery",
    "",
    "Agent sessions work inside a dedicated checkout of the repository",
    "that persists between sessions. If a session crashes before",
    "returning to the default branch, the next session inherits a",
    "stale branch. Every agent runs the following decision tree at",
    "session start, **before** any other work:",
    "",
    "| Clone state at session start | Action |",
    "|------------------------------|--------|",
    "| On the default branch | Normal flow — proceed. |",
    "| On a branch, no local changes, no new commits | Delete the branch and check out the default branch. |",
    "| On a branch, uncommitted changes | Stash the changes (retained in the clone for inspection) and check out the default branch. |",
    "| On a branch, commits exist, no open PR | Push the branch, leave a comment on the linked issue citing the branch name, then check out the default branch. |",
    "| On a branch, open PR exists | Check out the default branch — the work is already submitted. |",
    "",
    "### Resume hand-off",
    "",
    "After clone-level recovery returns the checkout to the default",
    "branch, the orchestrator's stale-issue recovery may reset the",
    "crashed issue to `status:ready`. The next worker session picks",
    "up the issue, reads the progress file from the working branch",
    "(e.g. via `git show origin/<branch>:<progress-file-path>`), and",
    "continues from the `next_action` recorded there.",
    "",
    `**Stale threshold:** a branch whose progress file's`,
    `\`last_updated\` is older than **${hours} hours** and that has`,
    "no matching open PR is considered abandoned. Operators may flip",
    "the issue back to `status:ready` so a fresh session can claim it;",
    "the partial-resume protocol above ensures the new session picks",
    "up the partial deliverable rather than starting over.",
  ];
}

function blockedCommentSection(): Array<string> {
  return [
    "## Blocked-Comment Format",
    "",
    "When an agent cannot proceed — missing upstream file, ambiguous",
    "dependency, unresolved human decision — it posts a structured",
    "`[BLOCKED]` comment on the issue before stopping:",
    "",
    "```",
    "[BLOCKED] Reason: <description>",
    "Depends on: #<N> (if formal dependency) or",
    '  "No formal dependency — needs human input"',
    "Expected resolution: <what needs to happen to unblock>",
    "```",
    "",
    "The `Depends on:` line uses the repository's standard dependency",
    "syntax so the block integrates with the orchestrator's dependency",
    "sweep. Blocks with no formal dependency are escalated by the",
    "orchestrator's triage walk after the project's ambiguous-block",
    "threshold elapses.",
  ];
}

function assertValidStateDir(value: string): void {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(
      `ProgressFilesConfig.stateDir must be a non-empty string; got ${JSON.stringify(
        value,
      )}`,
    );
  }
  if (value.startsWith("/")) {
    throw new Error(
      `ProgressFilesConfig.stateDir must be a repo-relative path (no leading '/'); got ${JSON.stringify(
        value,
      )}`,
    );
  }
}

function assertValidFilenamePattern(value: string): void {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(
      `ProgressFilesConfig.filenamePattern must be a non-empty string; got ${JSON.stringify(
        value,
      )}`,
    );
  }
  if (!value.includes("<ISSUE_NUMBER>")) {
    throw new Error(
      `ProgressFilesConfig.filenamePattern must contain the '<ISSUE_NUMBER>' placeholder; got ${JSON.stringify(
        value,
      )}`,
    );
  }
  if (value.includes("/")) {
    throw new Error(
      `ProgressFilesConfig.filenamePattern must not contain '/' — use 'stateDir' for the directory; got ${JSON.stringify(
        value,
      )}`,
    );
  }
}

function assertValidFormat(value: string): void {
  if (!PROGRESS_FILES_FORMAT_VALUES.includes(value as "json" | "markdown")) {
    throw new Error(
      `ProgressFilesConfig.format must be one of ${PROGRESS_FILES_FORMAT_VALUES.join(
        ", ",
      )}; got ${JSON.stringify(value)}`,
    );
  }
}

function assertValidStaleAfterHours(value: number): void {
  if (
    typeof value !== "number" ||
    !Number.isInteger(value) ||
    value <= 0 ||
    !Number.isFinite(value)
  ) {
    throw new Error(
      `ProgressFilesConfig.staleAfterHours must be a positive integer; got ${JSON.stringify(
        value,
      )}`,
    );
  }
}
